import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Package,
  PackageX,
  Receipt,
  UsersRound,
} from 'lucide-react';

import { usePageContext } from '@/hooks/usePageContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import {
  listLowStockProducts,
  listProducts,
  listCategories,
  listProductTypes,
  type Product,
} from '@/services/inventory.service';
import StockStatusBadge from './StockStatusBadge';
import { listFacilities } from '@/services/admin.service';
import type { FilterOption } from '@/types/filters.types';
import {
  listPurchaseOrders,
  type POStatus,
  type PurchaseOrder,
} from '@/services/purchase-orders.service';
import { listVendors } from '@/services/vendors.service';
import {
  INVENTORY_DASHBOARD_FILTER_CONFIGS,
  INVENTORY_STATUS_OPTIONS,
  INVENTORY_STOCK_LEVEL_OPTIONS,
  useFacilityOverrideValues,
  useLocalDashboardFilters,
} from '@/pages/dashboard/dashboardFilters';
import { buildApiParams } from '@/lib/filterRegistry';

const PO_STATUS_LABELS: Record<POStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  partially_received: 'Partially Received',
  received: 'Received',
  cancelled: 'Cancelled',
};

const PO_STATUS_BADGES: Record<POStatus, string> = {
  draft: 'bg-slate-100 text-slate-700',
  submitted: 'bg-blue-100 text-blue-700',
  partially_received: 'bg-amber-100 text-amber-700',
  received: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
}

function LoadingCopy({ message }: { message: string }) {
  return <p className="py-6 text-sm text-slate-400">{message}</p>;
}

function EmptyCopy({ message }: { message: string }) {
  return <p className="py-6 text-sm text-slate-400">{message}</p>;
}

function ProductAlertList({
  items,
  emptyMessage,
}: {
  items: Product[];
  emptyMessage: string;
}) {
  if (items.length === 0) {
    return <EmptyCopy message={emptyMessage} />;
  }

  return (
    <div className="divide-y divide-slate-100">
      {items.map((product) => (
        <div key={product.productId} className="flex items-start justify-between gap-4 py-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="text-sm font-medium text-slate-900 break-words">{product.name}</p>
              <StockStatusBadge status={product.stockStatus} />
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {product.category ?? 'Uncategorized'}
            </p>
          </div>
          <div className="shrink-0 text-right text-xs text-slate-600">
            {/* Negative on-hand is shown as-is — it is the size of the gap between
                what was consumed and what was ever booked in. */}
            <p className={product.stockQuantity <= 0 ? 'font-medium text-red-700' : 'font-medium text-slate-800'}>
              {product.stockQuantity.toLocaleString()} on hand
            </p>
            <p>{product.lowStockAlert > 0 ? `Alert at ${product.lowStockAlert}` : 'No alert level set'}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function PurchaseOrderAttentionList({ items }: { items: PurchaseOrder[] }) {
  if (items.length === 0) {
    return <EmptyCopy message="No submitted or partially received purchase orders." />;
  }

  return (
    <div className="divide-y divide-slate-100">
      {items.map((po) => (
        <div key={po.purchaseOrderId} className="flex items-start justify-between gap-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-900">
              {po.orderNumber?.trim() || `PO #${po.purchaseOrderId}`}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {po.vendorName || 'Vendor not assigned'}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${PO_STATUS_BADGES[po.status]}`}
            >
              {PO_STATUS_LABELS[po.status]}
            </span>
            <p className="mt-1 text-xs text-slate-500">
              Expected {formatDate(po.expectedDate)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function InventoryDashboardPage() {
  usePageContext('Inventory Dashboard');
  const navigate = useNavigate();
  const filters = useLocalDashboardFilters(INVENTORY_DASHBOARD_FILTER_CONFIGS, 'inventory-stock');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filters.values);
  const productFilters = useMemo(
    () => buildApiParams(effectiveValues, filters.negations),
    [effectiveValues, filters.negations],
  );

  // `active: true` so the tile matches its "Active Products" label — it
  // previously reported the total for whatever filters happened to be set.
  const productsSummaryQuery = useQuery({
    queryKey: ['inventory-dashboard-products-total', productFilters],
    queryFn: () => listProducts({ active: true, ...productFilters, page: 1, perPage: 1 }),
    staleTime: 60_000,
  });

  const vendorsSummaryQuery = useQuery({
    queryKey: ['inventory-dashboard-vendors-total'],
    queryFn: () => listVendors({ active: true, page: 1, perPage: 1 }),
    staleTime: 60_000,
  });

  // Out of stock + below reorder threshold, most-depleted first (ordered server-side).
  const attentionQuery = useQuery({
    queryKey: ['inventory-dashboard-low-stock', productFilters],
    queryFn: () => listLowStockProducts(productFilters),
    staleTime: 60_000,
  });

  const { data: categories } = useQuery({
    queryKey: ['inventory-dashboard-categories'],
    queryFn: listCategories,
    staleTime: 5 * 60_000,
  });

  const { data: productTypes } = useQuery({
    queryKey: ['inventory-dashboard-product-types'],
    queryFn: listProductTypes,
    staleTime: 5 * 60_000,
  });

  const { data: facilities } = useQuery({
    queryKey: ['inventory-dashboard-facilities'],
    queryFn: listFacilities,
    staleTime: 5 * 60_000,
  });

  const submittedPOQuery = useQuery({
    queryKey: ['inventory-dashboard-po-submitted'],
    queryFn: () => listPurchaseOrders({ status: 'submitted', page: 1, perPage: 1 }),
    staleTime: 60_000,
  });

  // Filtered server-side. The old version pulled the first 8 POs of any status
  // and filtered client-side, so submitted POs past that window went unreported.
  const attentionPOQuery = useQuery({
    queryKey: ['inventory-dashboard-po-attention'],
    queryFn: () => listPurchaseOrders({ status: 'submitted', page: 1, perPage: 5 }),
    staleTime: 60_000,
  });

  const attentionItems = useMemo(
    () => (attentionQuery.data ?? []).slice(0, 5),
    [attentionQuery.data],
  );

  const attentionPOs = useMemo(
    () => attentionPOQuery.data?.data ?? [],
    [attentionPOQuery.data],
  );

  const totalProducts = productsSummaryQuery.data?.meta.total ?? 0;
  const totalVendors = vendorsSummaryQuery.data?.meta.total ?? 0;
  const outOfStockCount = (attentionQuery.data ?? []).filter((p) => p.stockStatus === 'out').length;
  const lowStockCount = (attentionQuery.data ?? []).filter((p) => p.stockStatus === 'low').length;
  const submittedCount = submittedPOQuery.data?.meta.total ?? 0;
  const filterOptions = useMemo<Record<string, FilterOption[]>>(() => ({
    category: (categories ?? []).map((category) => ({ value: category, label: category })),
    productType: (productTypes ?? []).map((type) => ({
      value: String(type.productTypeID),
      label: type.productTypePublicName || type.productTypeName || `Type #${type.productTypeID}`,
    })),
    facility: (facilities ?? []).filter((facility) => facility.active).map((facility) => ({
      value: String(facility.facilityId),
      label: facility.facilityName || 'Unknown',
    })),
    status: INVENTORY_STATUS_OPTIONS,
    stockLevel: INVENTORY_STOCK_LEVEL_OPTIONS,
  }), [categories, facilities, productTypes]);

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={INVENTORY_DASHBOARD_FILTER_CONFIGS}
          values={effectiveValues}
          options={filterOptions}
          negations={filters.negations}
          onChange={filters.setFilter}
          onNegateChange={filters.setNegation}
          onClear={filters.clearFilter}
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          storageKey="inventory-stock"
          showAIAssistant={false}
        />
      )}
    >
      <div className="space-y-6 overflow-y-auto p-6">
      <PageHeader
        title="Inventory"
        actions={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => navigate('/inventory/stock')}>
              Open Stock
            </Button>
            <Button variant="secondary" onClick={() => navigate('/inventory/purchase-orders')}>
              Open Purchase Orders
            </Button>
            <Button onClick={() => navigate('/inventory/reports')}>
              Open Reports
            </Button>
          </div>
        )}
      />

      <MetricCardGrid columns={5}>
        <StatCard
          label="Active Products"
          value={totalProducts}
          tone="blue"
          icon={<Package className="h-4 w-4" />}
          onClick={() => navigate('/inventory/stock')}
        />
        <StatCard
          label="Active Vendors"
          value={totalVendors}
          tone="violet"
          icon={<UsersRound className="h-4 w-4" />}
          onClick={() => navigate('/inventory/vendors')}
        />
        <StatCard
          label="Out of Stock"
          value={outOfStockCount}
          tone="red"
          icon={<PackageX className="h-4 w-4" />}
          onClick={() => navigate('/inventory/stock')}
        />
        <StatCard
          label="Low Stock"
          value={lowStockCount}
          tone="amber"
          icon={<AlertTriangle className="h-4 w-4" />}
          onClick={() => navigate('/inventory/stock')}
        />
        <StatCard
          label="Submitted POs"
          value={submittedCount}
          tone="sky"
          icon={<Receipt className="h-4 w-4" />}
          onClick={() => navigate('/inventory/purchase-orders')}
        />
      </MetricCardGrid>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Button variant="secondary" fullWidth onClick={() => navigate('/inventory/stock')}>
                Stock Workspace
              </Button>
              <Button variant="secondary" fullWidth onClick={() => navigate('/inventory/vendors')}>
                Vendors
              </Button>
              <Button variant="secondary" fullWidth onClick={() => navigate('/inventory/purchase-orders')}>
                Purchase Orders
              </Button>
              <Button variant="secondary" fullWidth onClick={() => navigate('/inventory/reports')}>
                Reports
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Purchase Orders Requiring Attention</CardTitle>
          </CardHeader>
          <CardBody>
            {attentionPOQuery.isLoading ? (
              <LoadingCopy message="Loading purchase orders..." />
            ) : (
              <PurchaseOrderAttentionList items={attentionPOs} />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Stock Needing Attention</CardTitle>
          </CardHeader>
          <CardBody>
            {attentionQuery.isLoading ? (
              <LoadingCopy message="Loading stock alerts..." />
            ) : (
              <ProductAlertList
                items={attentionItems}
                emptyMessage="Nothing out of stock or below its reorder level."
              />
            )}
          </CardBody>
        </Card>
      </div>
      </div>
    </ListPageLayout>
  );
}
